import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useMatch } from "react-router-dom";
import useAsync from "react-use/esm/useAsync";

import { stringifyEntityRef } from "@backstage/catalog-model";
import { useApi } from "@backstage/core-plugin-api";
import { catalogApiRef } from "@backstage/plugin-catalog-react";
import { type TemplateEntityV1beta3 } from "@backstage/plugin-scaffolder-common";
import { type Defineable } from "@internal/backstage-plugin-utility-common";

/** The template entity for the current scaffolder template page. */
export type TemplateEntityState = {
    entity: Defineable<TemplateEntityV1beta3>;
    loading: boolean;
};

const TemplateEntityContext =
    createContext<Defineable<TemplateEntityState>>(undefined);

type Props = { children: ReactNode };

/**
 * Looks up the catalog entity for the current
 * `/create/templates/:namespace/:templateName` route once and shares it with
 * the lifecycle guards rendered inside it, so they don't each fetch it.
 *
 * Outside a template page there's nothing to look up, so `entity` is
 * `undefined`.
 *
 * @param props - {@link Props}
 */
export function TemplateEntityProvider({ children }: Props) {
    const match = useMatch("/create/templates/:namespace/:templateName");
    const catalogApi = useApi(catalogApiRef);

    const { value: entity, loading } = useAsync(async () => {
        if (!match) return undefined;
        const { namespace, templateName } = match.params;
        return (await catalogApi.getEntityByRef(
            stringifyEntityRef({
                kind: "Template",
                namespace: namespace!,
                name: templateName!,
            })
        )) as Defineable<TemplateEntityV1beta3>;
    }, [match?.params.namespace, match?.params.templateName]);

    const state = useMemo(() => ({ entity, loading }), [entity, loading]);

    return (
        <TemplateEntityContext.Provider value={state}>
            {children}
        </TemplateEntityContext.Provider>
    );
}

/**
 * Returns the template entity shared by the nearest
 * {@link TemplateEntityProvider}.
 */
export function useTemplateEntity(): TemplateEntityState {
    const state = useContext(TemplateEntityContext);
    if (!state) {
        throw new Error(
            "useTemplateEntity must be used inside a TemplateEntityProvider"
        );
    }
    return state;
}
